import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiDefaults } from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { environment as angularEnvironment } from '@lsq-frontend/fasttrack-shared/environment';
import { atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    DashboardClaim,
    Environment,
    Guid,
    ThirdPartyReferenceType
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    ClientSettingType,
    CompanyDetail,
    GetClientSettingRequest,
    GetClientSettingResponse,
    GetClientSettingService,
    GetRelationshipsByClientService,
    ImpersonateUserRequest,
    ImpersonateUserService,
    InvoiceBatchUploadMethod,
    ListCompaniesService,
    ListCompanyDto,
    ListInvoiceBatchUploadMethodsRequest,
    ListInvoiceBatchUploadMethodsService,
    ListPeopleDetailForClientService,
    PersonDetail,
    ThirdPartyReference,
    UpdateClientSettingTerminatedRequest,
    UpdateClientSettingTerminatedService,
    UpdateInvoiceBatchUploadMethodsRequest,
    UpdateInvoiceBatchUploadMethodsService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    forLifeOf,
    LifecycleAware,
    LsqSearchComponent,
    Orientation,
    getDynamicRouteLink
} from '@lsq-frontend/common-ui';
import { FeatureFlagService, getRouterLinkUrlById, Icons } from '@lsq-frontend/common-ui';
import { Modal, ModalService } from '@lsq-frontend/common-ui';
import { Observable } from 'rxjs';
import { distinctUntilKeyChanged, map, tap } from 'rxjs/operators';
import { IImpersonateUser } from '../../settings/users/users.component';
import {
    DashboardExperience,
    DashboardExperienceDisplayEnum
} from './../../../constants/experience.constants';

@LifecycleAware()
@Component({
    selector: 'client-management',
    template: `
        <div class="client-management">
            <page-header titleText="Admin Overview">
                <div class="admin-user-management-button" *ngIf="adminUserManagementIsOn$ | async">
                    <lsq-button
                        (buttonClick)="navigateToAdminUserManagement()"
                        appearance="secondary"
                    >
                        Admin User Management
                    </lsq-button>
                </div>
            </page-header>
            <div class="container-fluid page-container">
                <card [headerTitle]="'Client Management'">
                    <div right-header-content>
                        <lsq-button
                            id="dev-login"
                            *ngIf="isDevelopmentLoginEnabled"
                            class="development-login-button"
                            (buttonClick)="developmentLogin()"
                            appearance="secondary"
                        >
                            Dev Login
                        </lsq-button>
                    </div>
                    <div card-body>
                        <div class="search-section management-section">
                            <form [formGroup]="formGroup">
                                <lsq-search
                                    #customerSearch
                                    name="customerSearch"
                                    placeholder="Search Clients/Suppliers"
                                    formControlName="customerSearchControl"
                                    primaryDisplayKey="legalName"
                                    [searchService]="listCompaniesService"
                                    [group]="formGroup"
                                    [alwaysShowClose]="true"
                                    [largeInlineStyling]="false"
                                    [allowIneligible]="true"
                                    [requestAdditionalFields]="{
                                        includeReadOnly: includeReadOnly,
                                        includeDemo: includeDemo
                                    }"
                                    [primaryKey]="'companyKey'"
                                    [secondaryDisplayKey]="'companyNumber'"
                                    [secondaryDisplayMethod]="getSecondaryDisplayForCompanyBound"
                                    [isSecondaryDataAddress]="false"
                                >
                                </lsq-search>
                            </form>
                            <lsq-checkbox
                                [id]="'include-read-only-checkbox'"
                                [(ngModel)]="includeReadOnly"
                                [label]="'Include Read Only'"
                            >
                            </lsq-checkbox>
                            <lsq-checkbox
                                [id]="'include-demo-checkbox'"
                                [(ngModel)]="includeDemo"
                                [label]="'Include Demo Platform'"
                            >
                            </lsq-checkbox>
                        </div>

                        <div class="settings-section management-section" *ngIf="selectedCompanyKey">
                            <div
                                class="client-settings"
                                *ngIf="
                                    clientDashboardExperiences.includes(
                                        dashboardExperience.Factoring
                                    )
                                "
                            >
                                <div class="section-header">Client Settings</div>
                                <div class="section-subheader">
                                    Set global settings for this client.
                                </div>
                                <lsq-circle-loader *ngIf="isFetchingClientDetails">
                                </lsq-circle-loader>
                                <form
                                    *ngIf="!isFetchingClientDetails"
                                    [formGroup]="readOnlyFormGroup"
                                >
                                    <lsq-switch
                                        name="client-read-only"
                                        [labelOrientation]="orientation.Right"
                                        label="Read-Only"
                                        [group]="readOnlyFormGroup"
                                        formControlName="readOnly"
                                    >
                                    </lsq-switch>
                                </form>
                                <form
                                    *ngIf="!isFetchingClientDetails"
                                    [formGroup]="clientSettingsFormGroup"
                                >
                                    <lsq-switch
                                        name="client-flatfile"
                                        [labelOrientation]="orientation.Right"
                                        label="Client can use Flatfile"
                                        [group]="clientSettingsFormGroup"
                                        formControlName="flatFileUpload"
                                    >
                                    </lsq-switch>
                                </form>
                            </div>
                            <div class="quick-login">
                                <div class="section-header">Login without user</div>
                                <div class="section-subheader">
                                    Login with max permissions available. Note that you will not be
                                    tied to a specific user and may put the product into a situation
                                    it wasn’t designed for. Only use this if you know what you are
                                    doing.
                                </div>
                                <lsq-button
                                    size="md"
                                    appearance="secondary"
                                    (buttonClick)="quickLogin()"
                                    class="quick-login-button"
                                >
                                    Login without user
                                </lsq-button>
                            </div>
                            <div class="open-document-center" *claim="claim.AdminViewDocument">
                                <lsq-button
                                    *featureFlag="featureFlags.DocumentCenter"
                                    size="md"
                                    appearance="primary"
                                    (buttonClick)="navigateToAdminDocumentCenter()"
                                >
                                    Open Document Center
                                </lsq-button>
                            </div>
                        </div>

                        <div class="users-section" *ngIf="selectedCompanyKey">
                            <users
                                [isAdmin]="true"
                                [clientDashboardExperiences]="clientDashboardExperiences"
                                [companyKey]="selectedCompanyKey"
                                (impersonateUser)="impersonateUser($event)"
                            >
                            </users>
                        </div>
                    </div>
                </card>
            </div>
        </div>
    `,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./client-management.component.scss']
})
export class ClientManagementComponent implements OnInit, OnDestroy {
    @ViewChild('customerSearch') public customerSearch: LsqSearchComponent;

    private _invoiceBatchUploadMethods: InvoiceBatchUploadMethod[];

    public icons = Icons;
    public orientation = Orientation;
    public dashboardExperience = DashboardExperience;
    public formGroup: FormGroup;
    public isFetchingUserDetails = true;
    public isFetchingClientDetails = true;
    public isFetchingFlatFileSetting = true;
    public isFetchingReadOnly = true;
    public selectedCompanyKey: Guid = null;
    public selectedClientName = '';
    public clientDashboardExperiences: DashboardExperience[] = [];
    public flatFileSettingStream: Observable<InvoiceBatchUploadMethod[]>;
    public readOnlySettingStream: Observable<GetClientSettingResponse>;
    public clientSettingsFormGroup: FormGroup;
    public readOnlyFormGroup: FormGroup;
    public userDetailsStream: Observable<PersonDetail[]>;
    public isDevelopmentLoginEnabled = false;
    public includeReadOnly = false;
    public includeDemo = false;
    public getSecondaryDisplayForCompanyBound = this.getSecondaryDisplayForCompany.bind(this);
    public adminUserManagementIsOn$ = this._featureFlagService.isOn(
        FeatureFlags.AdminUserManagement
    );
    public claim = DashboardClaim;
    public featureFlags = FeatureFlags;
    constructor(
        public getRelationshipsByClientService: GetRelationshipsByClientService,
        public listPeopleDetailForClientService: ListPeopleDetailForClientService,
        public listCompaniesService: ListCompaniesService,
        private _listInvoiceBatchUploadMethodsService: ListInvoiceBatchUploadMethodsService,
        private _getClientSettingService: GetClientSettingService,
        private _updateInvoiceBatchUploadMethodsService: UpdateInvoiceBatchUploadMethodsService,
        private _updateClientSettingTerminatedService: UpdateClientSettingTerminatedService,
        private _modalService: ModalService,
        private _impersonateUserService: ImpersonateUserService,
        private _userService: UserService,
        private _router: Router,
        private _featureFlagService: FeatureFlagService
    ) {}

    public ngOnInit(): void {
        if (angularEnvironment.environment === Environment.Development) {
            this.isDevelopmentLoginEnabled = true;
        }

        this.formGroup = new FormGroup({
            customerSearchControl: new FormControl(null, [])
        });

        this.formGroup.valueChanges
            .pipe(forLifeOf(this), distinctUntilKeyChanged('customerSearchControl'))
            .subscribe(async (value) => {
                if (value.customerSearchControl) {
                    this.selectedCompanyKey = value.customerSearchControl;
                    this.selectedClientName = this.customerSearch.getSelectedResultPrimaryDisplayText();
                    this.clientDashboardExperiences = this._getClientExperiences(
                        this.customerSearch.selectedResult as CompanyDetail
                    );
                    this.getClientSettings();
                } else {
                    this.selectedCompanyKey = null;
                    this.selectedClientName = '';
                    this.clientDashboardExperiences = [];
                }
            });

        this.clientSettingsFormGroup = new FormGroup({
            flatFileUpload: new FormControl(false, [])
        });

        this.readOnlyFormGroup = new FormGroup({
            readOnly: new FormControl(false, [])
        });

        // Define the `userDetailsStream`.
        this.userDetailsStream = this.listPeopleDetailForClientService.dataStream.pipe(
            map((response) => response.rows),
            tap(() => (this.isFetchingUserDetails = false))
        );

        // Define the `clientSettingsStream`.
        this.flatFileSettingStream = this._listInvoiceBatchUploadMethodsService.dataStream.pipe(
            map((response) => response.rows),
            tap(() => {
                this.isFetchingFlatFileSetting = false;
                if (!this.isFetchingReadOnly) {
                    this.isFetchingClientDetails = false;
                }
            })
        );

        this.flatFileSettingStream
            .pipe(forLifeOf(this))
            .subscribe((invoiceBatchUploadMethods: InvoiceBatchUploadMethod[]) => {
                this._invoiceBatchUploadMethods = invoiceBatchUploadMethods;

                this.clientSettingsFormGroup.setValue(
                    {
                        flatFileUpload: this._invoiceBatchUploadMethods.includes(
                            InvoiceBatchUploadMethod.SpreadsheetAndBackupUpload
                        )
                    },
                    {
                        emitEvent: false
                    }
                );
            });

        this.readOnlySettingStream = this._getClientSettingService.dataStream.pipe(
            tap(() => {
                this.isFetchingReadOnly = false;
                if (!this.isFetchingFlatFileSetting) {
                    this.isFetchingClientDetails = false;
                }
            })
        );

        this.readOnlySettingStream
            .pipe(forLifeOf(this))
            .subscribe((response: GetClientSettingResponse) => {
                if (response && response.clientSetting) {
                    this.setReadOnlyFormValue(response.clientSetting.active);
                }
            });

        this.clientSettingsFormGroup.valueChanges.pipe(forLifeOf(this)).subscribe((value) => {
            if (value.flatFileUpload) {
                this._invoiceBatchUploadMethods.push(
                    InvoiceBatchUploadMethod.SpreadsheetAndBackupUpload
                );
            } else {
                this._invoiceBatchUploadMethods = this._invoiceBatchUploadMethods.filter(
                    (invoiceBatchUploadMethod) =>
                        invoiceBatchUploadMethod !==
                        InvoiceBatchUploadMethod.SpreadsheetAndBackupUpload
                );
            }
            const request = new UpdateInvoiceBatchUploadMethodsRequest({
                companyKey: this.selectedCompanyKey,
                clientInvoiceBatchUploadMethods: this._invoiceBatchUploadMethods
            });
            this._updateInvoiceBatchUploadMethodsService.execute(request);
        });

        // Error handling.
        this._updateInvoiceBatchUploadMethodsService.errorStream
            .pipe(forLifeOf(this))
            .subscribe(() => {
                this.clientSettingsFormGroup.setValue(
                    {
                        flatFileUpload: !this.clientSettingsFormGroup.get('flatFileUpload').value
                    },
                    {
                        emitEvent: false
                    }
                );
            });

        this.readOnlyFormGroup.valueChanges.pipe(forLifeOf(this)).subscribe((value) => {
            const bodyCopy = value.readOnly
                ? 'The users of read-only accounts will be able to log into Dashboard, ' +
                  'but will be unable to complete actions such as requesting funds, ' +
                  'credit or submitting invoices.'
                : 'Removing read-only will allow users to complete actions such as ' +
                  'requesting funds, credit or submitting invoices.';

            const headerCopy = value.readOnly ? 'Turn on Read-only' : 'Turn off Read-Only';

            this._modalService.showModal(
                new Modal({
                    id: 'terminate-client-modal',
                    title: headerCopy,
                    bodyText: bodyCopy,
                    showFooter: true,
                    okButtonText: 'Update',
                    cancelButtonText: 'Cancel',
                    showOkButton: true,
                    showCancelButton: true,
                    clickOutsideToClose: false,
                    onOkClick: () => {
                        this.updateClientTerminated(value.readOnly);
                        this._modalService.closeModal();
                    },
                    onCancelClick: () => {
                        this.setReadOnlyFormValue(!this.readOnlyFormGroup.get('readOnly').value);
                        this._modalService.closeModal();
                    }
                })
            );
        });

        // Error handling.
        this._updateClientSettingTerminatedService.errorStream
            .pipe(forLifeOf(this))
            .subscribe(() => {
                this.setReadOnlyFormValue(this.readOnlyFormGroup.get('readOnly').value);
            });
    }

    public ngOnDestroy(): void {}

    public navigateToAdminUserManagement(): void {
        this._router.navigateByUrl(getRouterLinkUrlById(atlas, 'ADMIN_USER_MANAGEMENT'));
    }

    public navigateToAdminDocumentCenter(): void {
        const adminDocsLink = getRouterLinkUrlById(atlas, 'ADMIN_DOCUMENTS');
        const companyKey = this.selectedCompanyKey;
        const companyName = this.selectedClientName;
        const dynamicLink = getDynamicRouteLink(adminDocsLink, { companyKey, companyName });
        this._router.navigateByUrl(dynamicLink);
    }

    private getClientSettings(): void {
        // If the selected company is not a factoring client, then skip client settings.
        if (!this.clientDashboardExperiences.includes(DashboardExperience.Factoring)) {
            return;
        }

        this.isFetchingClientDetails = true;
        this.isFetchingFlatFileSetting = true;
        this.isFetchingReadOnly = true;

        const flatFileSettingRequest = new ListInvoiceBatchUploadMethodsRequest({
            take: ApiDefaults.TakeAll,
            companyKey: this.selectedCompanyKey
        });
        this._listInvoiceBatchUploadMethodsService.execute(flatFileSettingRequest);

        const readOnlySettingRequest = new GetClientSettingRequest({
            companyKey: this.selectedCompanyKey,
            clientSettingType: ClientSettingType.Terminated
        });

        this._getClientSettingService.execute(readOnlySettingRequest);
    }

    public async developmentLogin(): Promise<void> {
        await this.impersonateUser({
            companyKey: '00000000-0000-0000-0000-000000000002', // Test Client 1
            userKey: '00000000-0000-0000-0000-000000000413' // Test Developer
        });
    }

    public async quickLogin(): Promise<void> {
        await this.impersonateUser({
            companyKey: this.selectedCompanyKey
        });
    }

    public async impersonateUser(userObject: IImpersonateUser): Promise<void> {
        await this._impersonateUserService.executeOnce(
            new ImpersonateUserRequest({
                companyKey: userObject.companyKey,
                userKey: userObject.userKey
            })
        );
        await this._userService.getAndEmitCurrentUser();
        this._userService.navigateToHome();
    }

    public getSecondaryDisplayForCompany(company: ListCompanyDto): string {
        const experiences = this._getClientExperiences(company);
        const experienceDisplays = experiences.map((experience) => {
            switch (experience) {
                case DashboardExperience.Factoring:
                case DashboardExperience.SCF_Account:
                case DashboardExperience.SCF_Buyer:
                    return `(${DashboardExperienceDisplayEnum[experience]})`;
                case DashboardExperience.SCF_Seller:
                    if (company.buyerName === '' || company.buyerName === null) {
                        return `(${DashboardExperienceDisplayEnum[experience]})`;
                    } else {
                        return `(${DashboardExperienceDisplayEnum[experience]} for ${company.buyerName})`;
                    }
            }
        });
        return experienceDisplays.join(', ');
    }

    private updateClientTerminated(isTerminated: boolean): void {
        const request = new UpdateClientSettingTerminatedRequest({
            companyKey: this.selectedCompanyKey,
            active: isTerminated
        });
        this._updateClientSettingTerminatedService.execute(request);
    }

    private setReadOnlyFormValue(readOnly: boolean): void {
        this.readOnlyFormGroup.setValue(
            {
                readOnly: readOnly
            },
            {
                emitEvent: false
            }
        );
    }

    private _getClientExperiences(company: CompanyDetail): DashboardExperience[] {
        const experiences = [];
        if (
            company.thirdPartyReferences.some(
                (thirdPartyReference: ThirdPartyReference) =>
                    thirdPartyReference.thirdPartyReferenceType ===
                    ThirdPartyReferenceType.Factorsoft
            )
        ) {
            experiences.push(DashboardExperience.Factoring);
        }
        if (
            company.thirdPartyReferences.some(
                (thirdPartyReference: ThirdPartyReference) =>
                    thirdPartyReference.thirdPartyReferenceType ===
                    ThirdPartyReferenceType.FinancingSeller
            )
        ) {
            experiences.push(DashboardExperience.SCF_Seller);
        }
        if (
            company.thirdPartyReferences.some(
                (thirdPartyReference: ThirdPartyReference) =>
                    thirdPartyReference.thirdPartyReferenceType ===
                    ThirdPartyReferenceType.FinancingBuyer
            )
        ) {
            experiences.push(DashboardExperience.SCF_Buyer);
        }
        if (
            company.thirdPartyReferences.some(
                (thirdPartyReference: ThirdPartyReference) =>
                    thirdPartyReference.thirdPartyReferenceType ===
                    ThirdPartyReferenceType.FinancingAccount
            )
        ) {
            experiences.push(DashboardExperience.SCF_Account);
        }
        return experiences;
    }
}
